import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

const Analytics = () => {
  const [data, setData] = useState(null);
  const [readingData, setReadingData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [res, readRes] = await Promise.all([
        axios.get(`${API}/analytics/personal`),
        axios.get(`${API}/analytics/reading-stats`),
      ]);
      setData(res.data);
      setReadingData(readRes.data);
    } catch (e) {
      console.error("Analytics fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex items-center justify-center">
        <div className="spinner-ember" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex items-center justify-center">
        <p className="text-[#8C7A5A]">Failed to load analytics.</p>
      </div>
    );
  }

  const maxGrowth = Math.max(...data.daily_growth.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-[#0A0600]" data-testid="analytics-page">
      {/* Header */}
      <header className="h-[56px] border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004] flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45" />
            <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
          </Link>
          <span className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px]">/ Analytics</span>
        </div>
        <Link to="/dashboard" className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] uppercase tracking-[1px] transition-colors">
          Back to Dashboard
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Entries", value: data.total_bookmarks, color: "#E8A030" },
            { label: "Volumes", value: data.total_collections, color: "#7AB8A0" },
            { label: "Uncategorized", value: data.uncategorized, color: "#C8600A" },
            { label: "Ask Ryan Queries", value: data.ask_ryan_queries, color: "#A08CC8" },
          ].map(s => (
            <div key={s.label} className="card-coke p-5 text-center" data-testid={`analytics-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="text-3xl font-bold font-serif" style={{ color: s.color }}>{s.value}</div>
              <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Growth Chart */}
        <div className="card-coke p-6">
          <h2 className="font-serif text-lg font-bold text-[#FDF8EE] mb-1">Entries — Last 30 Days</h2>
          <p className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] mb-5">
            {data.daily_growth.reduce((sum, d) => sum + d.count, 0)} entries added this month
          </p>
          <div className="flex items-end gap-[3px] h-32" data-testid="growth-chart">
            {data.daily_growth.map((day, i) => {
              const height = day.count > 0 ? Math.max((day.count / maxGrowth) * 100, 4) : 2;
              return (
                <div key={i} className="flex-1 group relative flex flex-col items-center justify-end">
                  <div
                    className={`w-full transition-all duration-200 ${day.count > 0 ? 'bg-[#E8A030] group-hover:bg-[#F5C040]' : 'bg-[rgba(232,160,48,0.08)]'}`}
                    style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '2px' }}
                    title={`${day.date}: ${day.count} entries`}
                  />
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                    <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.2)] px-2 py-1 whitespace-nowrap">
                      <div className="font-mono text-[8px] text-[#8C7A5A]">{day.date}</div>
                      <div className="font-mono text-[9px] text-[#E8A030] font-bold">{day.count} entries</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="font-mono text-[8px] text-[#4A3A1A]">{data.daily_growth[0]?.date}</span>
            <span className="font-mono text-[8px] text-[#4A3A1A]">{data.daily_growth[data.daily_growth.length - 1]?.date}</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Volumes */}
          <div className="card-coke p-6">
            <h2 className="font-serif text-lg font-bold text-[#FDF8EE] mb-4">Top Volumes</h2>
            {data.top_volumes.length === 0 ? (
              <p className="text-sm text-[#4A3A1A]">No volumes yet.</p>
            ) : (
              <div className="space-y-3">
                {data.top_volumes.map((vol, i) => {
                  const maxCount = data.top_volumes[0]?.count || 1;
                  return (
                    <div key={vol.collection_id} data-testid={`volume-stat-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span style={{ color: vol.color }}>◈</span>
                          <span className="text-sm text-[#FDF8EE]">{vol.name}</span>
                        </div>
                        <span className="font-mono text-[9px] text-[#E8A030]">{vol.count}</span>
                      </div>
                      <div className="w-full h-1.5 bg-[rgba(232,160,48,0.06)]">
                        <div className="h-full transition-all duration-500" style={{ width: `${(vol.count / maxCount) * 100}%`, backgroundColor: vol.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Tags */}
          <div className="card-coke p-6">
            <h2 className="font-serif text-lg font-bold text-[#FDF8EE] mb-4">Top Tags</h2>
            {data.top_tags.length === 0 ? (
              <p className="text-sm text-[#4A3A1A]">No tags used yet. Add tags to your bookmarks to see them here.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.top_tags.map(tag => (
                  <div key={tag.name} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[rgba(232,160,48,0.06)] border border-[rgba(232,160,48,0.12)] hover:border-[rgba(232,160,48,0.25)] transition-colors" data-testid={`tag-stat-${tag.name}`}>
                    <span className="text-[11px] text-[#E8A030]">#</span>
                    <span className="text-[11px] text-[#F5ECD0]">{tag.name}</span>
                    <span className="font-mono text-[8px] text-[#4A3A1A] ml-1">{tag.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card-coke p-6">
          <h2 className="font-serif text-lg font-bold text-[#FDF8EE] mb-4">Recent Activity</h2>
          {data.recent_activity.length === 0 ? (
            <p className="text-sm text-[#4A3A1A]">No activity yet.</p>
          ) : (
            <div className="space-y-3">
              {data.recent_activity.map((item, i) => (
                <div key={item.bookmark_id} className="flex items-start gap-3 py-2 border-b border-[rgba(232,160,48,0.06)] last:border-0" data-testid={`activity-${i}`}>
                  <div className="w-1.5 h-1.5 bg-[#E8A030] rotate-45 mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-[#8C7A5A] truncate">{item.content_preview}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">{item.collection_name}</span>
                      {item.tags?.length > 0 && item.tags.map(t => (
                        <span key={t} className="font-mono text-[8px] text-[#E8A030]">#{t}</span>
                      ))}
                    </div>
                  </div>
                  <span className="font-mono text-[8px] text-[#4A3A1A] shrink-0">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reading Stats */}
        {readingData && (
          <div className="space-y-6" data-testid="reading-stats-section">
            <h2 className="font-serif text-xl font-bold text-[#FDF8EE] flex items-center gap-3">
              <span className="text-[#7AB8A0]">&#9679;</span> Reading Stats
            </h2>

            {/* Reading summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Total Views", value: readingData.total_views, color: "#7AB8A0" },
                { label: "Unique Read", value: readingData.unique_viewed, color: "#E8A030" },
                { label: "Total Entries", value: readingData.total_bookmarks, color: "#A08CC8" },
                { label: "Revisit Rate", value: `${readingData.revisit_rate}%`, color: "#C86F3C" },
              ].map(s => (
                <div key={s.label} className="card-coke p-5 text-center" data-testid={`reading-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="text-3xl font-bold font-serif" style={{ color: s.color }}>{s.value}</div>
                  <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* View activity chart (14 days) */}
            {readingData.daily_views?.length > 0 && (
              <div className="card-coke p-6">
                <h3 className="font-serif text-lg font-bold text-[#FDF8EE] mb-1">Reading Activity — Last 14 Days</h3>
                <p className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] mb-5">
                  {readingData.daily_views.reduce((sum, d) => sum + d.count, 0)} views this period
                </p>
                <div className="flex items-end gap-[4px] h-28" data-testid="reading-chart">
                  {readingData.daily_views.map((day, i) => {
                    const maxV = Math.max(...readingData.daily_views.map(d => d.count), 1);
                    const height = day.count > 0 ? Math.max((day.count / maxV) * 100, 6) : 2;
                    return (
                      <div key={i} className="flex-1 group relative flex flex-col items-center justify-end">
                        <div
                          className={`w-full transition-all duration-200 ${day.count > 0 ? 'bg-[#7AB8A0] group-hover:bg-[#8ECDB3]' : 'bg-[rgba(122,184,160,0.08)]'}`}
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '2px' }}
                          title={`${day.date}: ${day.count} views`}
                        />
                        <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                          <div className="bg-[#1C1004] border border-[rgba(122,184,160,0.2)] px-2 py-1 whitespace-nowrap">
                            <div className="font-mono text-[8px] text-[#8C7A5A]">{day.date}</div>
                            <div className="font-mono text-[9px] text-[#7AB8A0] font-bold">{day.count} views</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="font-mono text-[8px] text-[#4A3A1A]">{readingData.daily_views[0]?.date}</span>
                  <span className="font-mono text-[8px] text-[#4A3A1A]">{readingData.daily_views[readingData.daily_views.length - 1]?.date}</span>
                </div>
              </div>
            )}

            {/* Most viewed bookmarks */}
            <div className="card-coke p-6">
              <h3 className="font-serif text-lg font-bold text-[#FDF8EE] mb-4">Most Revisited Bookmarks</h3>
              {readingData.most_viewed.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-[#4A3A1A]">No reading activity yet.</p>
                  <p className="text-[11px] text-[#4A3A1A] mt-1">Open, edit, or copy bookmarks to start tracking.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {readingData.most_viewed.map((bm, i) => {
                    const maxCount = readingData.most_viewed[0]?.view_count || 1;
                    return (
                      <div key={bm.bookmark_id} data-testid={`most-viewed-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="font-mono text-[9px] text-[#4A3A1A] w-4 text-right shrink-0">{i + 1}</span>
                            <p className="text-[12px] text-[#8C7A5A] truncate">{bm.content_preview}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-3">
                            <span className="font-mono text-[8px] text-[#4A3A1A] uppercase">{bm.collection_name}</span>
                            <span className="font-mono text-[9px] text-[#7AB8A0] font-bold">{bm.view_count}x</span>
                          </div>
                        </div>
                        <div className="w-full h-1 bg-[rgba(122,184,160,0.06)] ml-6" style={{ width: 'calc(100% - 24px)' }}>
                          <div className="h-full bg-[#7AB8A0] transition-all duration-500" style={{ width: `${(bm.view_count / maxCount) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Analytics;
